import { useEffect, useRef, useState } from 'react'
import {
  CheckCircle2,
  Copy,
  Check,
  ShoppingBag,
  ArrowRight,
  Clock,
  MapPin,
  Truck,
  Sparkles,
  X,
  CreditCard,
  Banknote,
  ShieldCheck,
} from 'lucide-react'
import { formatMoney } from '../api/client'
import { useTranslation } from '../i18n/LanguageContext'

export interface SuccessOrderDetails {
  orderId: string
  orderNumber: string
  totalPaise: number
  paymentMethod: string
  paymentStatus?: string
  fulfilmentType?: 'DELIVERY' | 'PICKUP'
  itemCount?: number
  deliveryAddress?: {
    recipientName?: string
    phoneNumber?: string
    formattedSingleLine?: string
  }
  window?: {
    slotId?: string
    date?: string
    label?: string
  }
}

export interface OrderSuccessModalProps {
  isOpen: boolean
  onClose: () => void
  order: SuccessOrderDetails | null
  onTrackOrder: (orderId: string) => void
  onContinueShopping: () => void
}

/**
 * Plays a cheerful, harmonic success chime via Web Audio API.
 * Completely standalone without requiring external sound files.
 */
function playSuccessChime() {
  try {
    const AudioContextClass =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
    if (!AudioContextClass) return

    const ctx = new AudioContextClass()
    if (ctx.state === 'suspended') {
      ctx.resume()
    }
    const now = ctx.currentTime

    // 4 ascending melodic tones (C5, E5, G5, C6)
    const tones = [
      { freq: 523.25, time: 0, duration: 0.16, gain: 0.1 },
      { freq: 659.25, time: 0.1, duration: 0.18, gain: 0.12 },
      { freq: 783.99, time: 0.22, duration: 0.26, gain: 0.14 },
      { freq: 1046.5, time: 0.36, duration: 0.45, gain: 0.16 },
    ]

    tones.forEach(({ freq, time, duration, gain }) => {
      const osc = ctx.createOscillator()
      const gainNode = ctx.createGain()

      osc.type = 'sine'
      osc.frequency.setValueAtTime(freq, now + time)

      gainNode.gain.setValueAtTime(0, now + time)
      gainNode.gain.linearRampToValueAtTime(gain, now + time + 0.02)
      gainNode.gain.exponentialRampToValueAtTime(0.0001, now + time + duration)

      osc.connect(gainNode)
      gainNode.connect(ctx.destination)

      osc.start(now + time)
      osc.stop(now + time + duration)
    })
  } catch {
    // Ignore audio errors if blocked by browser policy
  }
}

/**
 * High-performance lightweight Canvas Confetti component
 */
function ConfettiCanvas() {
  const canvasRef = useRef<HTMLCanvasElement | null>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas || typeof canvas.getContext !== 'function') return
    let ctx: CanvasRenderingContext2D | null = null
    try {
      ctx = canvas.getContext('2d')
    } catch {
      return
    }
    if (!ctx) return

    const width = (canvas.width = window.innerWidth)
    const height = (canvas.height = window.innerHeight)

    const colors = ['#10B981', '#059669', '#34D399', '#F59E0B', '#FBBF24', '#6366F1', '#EC4899', '#3B82F6']
    const particlesCount = 90
    const particles = Array.from({ length: particlesCount }, () => ({
      x: width * (0.3 + Math.random() * 0.4),
      y: height * 0.25 + (Math.random() - 0.5) * 40,
      vx: (Math.random() - 0.5) * 16,
      vy: -Math.random() * 12 - 4,
      size: Math.random() * 8 + 4,
      color: colors[Math.floor(Math.random() * colors.length)] || '#10B981',
      rotation: Math.random() * 360,
      rotationSpeed: (Math.random() - 0.5) * 10,
      shape: Math.random() > 0.4 ? 'rect' : 'circle',
      opacity: 1,
    }))

    let animationFrameId: number
    const startTime = Date.now()

    function render() {
      if (!ctx || !canvas) return
      ctx.clearRect(0, 0, width, height)

      const elapsed = Date.now() - startTime
      const globalAlpha = Math.max(0, 1 - elapsed / 4000)

      particles.forEach((p) => {
        p.x += p.vx
        p.y += p.vy
        p.vy += 0.35 // gravity
        p.vx *= 0.98 // air resistance
        p.rotation += p.rotationSpeed

        ctx.save()
        ctx.globalAlpha = p.opacity * globalAlpha
        ctx.translate(p.x, p.y)
        ctx.rotate((p.rotation * Math.PI) / 180)
        ctx.fillStyle = p.color

        if (p.shape === 'rect') {
          ctx.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2)
        } else {
          ctx.beginPath()
          ctx.arc(0, 0, p.size / 3, 0, Math.PI * 2)
          ctx.fill()
        }

        ctx.restore()
      })

      if (elapsed < 4200) {
        animationFrameId = requestAnimationFrame(render)
      } else {
        ctx.clearRect(0, 0, width, height)
      }
    }

    render()

    return () => {
      cancelAnimationFrame(animationFrameId)
    }
  }, [])

  return (
    <canvas
      ref={canvasRef}
      className="pointer-events-none fixed inset-0 z-[70] h-full w-full"
      aria-hidden="true"
    />
  )
}

export function OrderSuccessModal({
  isOpen,
  onClose,
  order,
  onTrackOrder,
  onContinueShopping,
}: OrderSuccessModalProps) {
  const [copied, setCopied] = useState(false)
  const [countdown, setCountdown] = useState(8)
  const [autoRedirectPaused, setAutoRedirectPaused] = useState(false)

  // Trigger celebration chime on open
  useEffect(() => {
    if (isOpen) {
      playSuccessChime()
      setCountdown(8)
      setAutoRedirectPaused(false)
    }
  }, [isOpen])

  // Auto-redirect countdown
  useEffect(() => {
    if (!isOpen || autoRedirectPaused || !order) return

    if (countdown <= 0) {
      onTrackOrder(order.orderId)
      return
    }

    const timer = setTimeout(() => {
      setCountdown((prev) => prev - 1)
    }, 1000)

    return () => clearTimeout(timer)
  }, [isOpen, countdown, autoRedirectPaused, order, onTrackOrder])

  // Close on ESC
  useEffect(() => {
    if (!isOpen) return
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, onClose])

  if (!isOpen || !order) return null

  const isOnline = order.paymentMethod === 'ONLINE' || order.paymentStatus === 'PAID'
  const isPickup = order.fulfilmentType === 'PICKUP'

  function copyOrderNumber() {
    if (!order) return
    navigator.clipboard.writeText(order.orderNumber)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  let isHi = false
  try {
    const i18n = useTranslation()
    isHi = i18n.isHindi
  } catch {
    // Keep English in standalone test runner
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="order-success-title"
      className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 overflow-y-auto"
    >
      {/* Confetti Explosion */}
      <ConfettiCanvas />

      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-stone-950/70 backdrop-blur-sm transition-opacity duration-300"
        onClick={onClose}
      />

      {/* Modal Dialog Card */}
      <div className="relative z-50 my-auto w-full max-w-lg overflow-hidden rounded-3xl bg-white shadow-2xl ring-1 ring-stone-900/10 transition-all transform animate-in fade-in zoom-in-95 duration-200">
        {/* Festive Header Gradient Banner */}
        <div className="h-3 bg-gradient-to-r from-emerald-500 via-teal-500 to-emerald-600" />

        {/* Close Button */}
        <button
          type="button"
          onClick={onClose}
          aria-label="Close confirmation dialog"
          className="absolute right-4 top-5 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-stone-100 text-stone-500 transition-colors hover:bg-stone-200 hover:text-stone-800"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="p-6 sm:p-7 space-y-5">
          {/* Animated Celebration Icon */}
          <div className="flex flex-col items-center text-center">
            <div className="relative mb-3.5 flex items-center justify-center">
              {/* Outer pulsing glow */}
              <div className="absolute h-20 w-20 rounded-full bg-emerald-500/20 animate-ping" />
              <div className="relative flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-tr from-emerald-600 to-teal-500 text-white shadow-xl shadow-emerald-600/30">
                <CheckCircle2 className="h-9 w-9 stroke-[2.5]" />
              </div>
            </div>

            {/* Reassuring Title & Subtitle */}
            <div className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-800 border border-emerald-200/60 mb-2">
              <Sparkles className="h-3.5 w-3.5 text-emerald-600" />
              <span>{isHi ? 'ऑर्डर सफलतापूर्वक प्राप्त हुआ! 🎉' : 'Order Placed Successfully! 🎉'}</span>
            </div>

            <h2
              id="order-success-title"
              className="font-heading text-2xl font-black text-stone-900 sm:text-3xl tracking-tight"
            >
              {isHi ? 'ऑर्डर कन्फर्म हो गया!' : 'Order Confirmed!'}
            </h2>

            {/* The primary reassurance message requested by user */}
            <p className="mt-1.5 text-sm sm:text-base font-semibold text-stone-700 leading-snug max-w-sm">
              {isHi ? 'आपका ऑर्डर कन्फर्म हो गया है और जल्द ही डिलीवर होगा!' : 'Your order will be confirmed and will deliver soon!'}
            </p>
            <p className="mt-0.5 text-xs text-stone-500">
              Aapka order confirm ho gaya hai aur jaldi hi aapke address par deliver hoga 🚀
            </p>
          </div>

          {/* Quick Highlight Cards */}
          <div className="space-y-3">
            {/* Live Delivery / Pickup Status Pill */}
            <div className="rounded-2xl border border-emerald-200/90 bg-emerald-50/60 p-3.5 sm:p-4 transition-all">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-600 text-white shadow-sm shadow-emerald-700/20">
                  {isPickup ? <ShoppingBag className="h-5 w-5" /> : <Truck className="h-5 w-5" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="flex h-2 w-2 rounded-full bg-emerald-600 animate-pulse" />
                    <h4 className="text-xs font-black uppercase tracking-wider text-emerald-900">
                      {isHi
                        ? isPickup
                          ? 'स्टोर पिकअप कन्फर्म हुआ'
                          : 'पैकिंग शुरू हो गई है · जल्द ही डिलीवरी के लिए निकलेगा'
                        : isPickup
                          ? 'Store Pickup Confirmed'
                          : 'Packing Started · Out for Delivery Soon'}
                    </h4>
                  </div>
                  <p className="mt-0.5 text-xs font-medium text-stone-700">
                    {order.window?.label ? (
                      <>
                        <Clock className="inline mr-1 h-3.5 w-3.5 text-emerald-700" />
                        <span>{isHi ? 'स्लॉट: ' : 'Slot: '}<strong className="text-stone-900 font-bold">{order.window.label}</strong></span>
                      </>
                    ) : (
                      isHi
                        ? 'हमने स्टोर को सूचित कर दिया है। हमारे डिलीवरी पार्टनर जल्द पहुंचेंगे!'
                        : 'We have notified the store. Our delivery partner will arrive soon!'
                    )}
                  </p>
                  {order.deliveryAddress?.formattedSingleLine && !isPickup && (
                    <p className="mt-1 text-[11px] text-stone-500 truncate flex items-center gap-1">
                      <MapPin className="h-3 w-3 shrink-0 text-stone-400" />
                      <span>{isHi ? 'डिलीवरी का पता: ' : 'Delivering to: '}<span className="font-semibold text-stone-700">{order.deliveryAddress.recipientName || (isHi ? 'आपको' : 'You')}</span> ({order.deliveryAddress.formattedSingleLine})</span>
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Payment Mode Highlight */}
            <div className="rounded-2xl border border-stone-200/90 bg-stone-50/80 p-3.5 text-xs">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className={`flex h-7 w-7 items-center justify-center rounded-lg ${isOnline ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
                    {isOnline ? <CreditCard className="h-4 w-4" /> : <Banknote className="h-4 w-4" />}
                  </div>
                  <div>
                    <span className="font-bold text-stone-900">
                      {isOnline ? 'Paid Online via Razorpay' : 'Cash on Delivery (COD)'}
                    </span>
                    <p className="text-[11px] text-stone-500">
                      {isOnline
                        ? 'Payment verified · Zero cash required upon delivery'
                        : isHi
                          ? 'कृपया कैश तैयार रखें या डिलीवरी पर UPI QR स्कैन करें'
                          : 'Please keep cash ready or scan UPI QR code on delivery'}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <span className="font-heading text-sm font-black text-stone-900">
                    {formatMoney(order.totalPaise)}
                  </span>
                  <span className={`block text-[10px] font-bold ${isOnline ? 'text-emerald-700' : 'text-amber-700'}`}>
                    {isOnline ? '✓ Verified' : isHi ? 'घर पर भुगतान' : 'Pay at Doorstep'}
                  </span>
                </div>
              </div>
            </div>

            {/* Order Reference Number */}
            <div className="flex items-center justify-between rounded-xl bg-stone-100/80 px-3.5 py-2 text-xs">
              <span className="font-medium text-stone-500">
                Order ID:{' '}
                <strong className="font-mono font-bold text-stone-900">
                  #{order.orderNumber}
                </strong>
              </span>
              <button
                type="button"
                onClick={copyOrderNumber}
                className="inline-flex items-center gap-1 rounded-lg px-2 py-1 font-semibold text-emerald-800 hover:bg-emerald-100/60 transition-colors"
              >
                {copied ? (
                  <>
                    <Check className="h-3.5 w-3.5 text-emerald-600" />
                    <span className="text-[11px] text-emerald-700 font-bold">Copied!</span>
                  </>
                ) : (
                  <>
                    <Copy className="h-3.5 w-3.5" />
                    <span className="text-[11px]">Copy ID</span>
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Auto Redirect Countdown Notice */}
          <div className="flex items-center justify-between rounded-xl bg-stone-50 px-3.5 py-2 text-[11px] text-stone-500">
            <span className="flex items-center gap-1.5">
              <ShieldCheck className="h-3.5 w-3.5 text-emerald-600" />
              {autoRedirectPaused ? (
                <span>Auto-redirect paused. Take your time!</span>
              ) : (
                <span>
                  Opening order tracker in <strong className="text-stone-800 font-bold">{countdown}s</strong>...
                </span>
              )}
            </span>
            <button
              type="button"
              onClick={() => setAutoRedirectPaused((p) => !p)}
              className="font-semibold text-stone-600 hover:text-stone-900 underline"
            >
              {autoRedirectPaused ? 'Resume timer' : 'Stay here'}
            </button>
          </div>

          {/* Action CTAs */}
          <div className="flex flex-col sm:flex-row gap-2.5 pt-1">
            <button
              type="button"
              onClick={() => onTrackOrder(order.orderId)}
              className="btn-primary flex-1 justify-center py-3 text-sm font-bold shadow-md shadow-emerald-700/20"
            >
              <span>{isHi ? 'ऑर्डर लाइव ट्रैक करें' : 'Track Live Order'}</span>
              <ArrowRight className="h-4 w-4" />
            </button>

            <button
              type="button"
              onClick={onContinueShopping}
              className="btn-secondary justify-center py-3 text-sm font-bold"
            >
              <ShoppingBag className="h-4 w-4 text-stone-500" />
              <span>{isHi ? 'और खरीदारी करें' : 'Continue Shopping'}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
